import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

const AddSongModal = ({ 
  open, 
  onClose, 
  onSave, 
  playlistId,
  targetSetId = null,
  availableSets = [],
  existingSongIds = []
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState(targetSetId);
  const [loadingSongs, setLoadingSongs] = useState(true);

  useEffect(() => {
    if (open) {
      fetchSongs();
      setSelectedSetId(targetSetId);
      setSelectedSongs([]);
      setSearchTerm('');
    }
  }, [open, targetSetId]);

  const fetchSongs = async () => {
    setLoadingSongs(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/songs`);
      if (response.ok) {
        const data = await response.json();
        // Filter out songs that are already in the playlist
        const filtered = data.filter(song => !existingSongIds.includes(song.songs_master_id));
        setSongs(filtered);
      } else {
        enqueueSnackbar('Failed to load songs', { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching songs:', error);
      enqueueSnackbar('Failed to load songs', { variant: 'error' });
    } finally {
      setLoadingSongs(false);
    }
  };

  const filteredSongs = useMemo(() => {
    if (!searchTerm.trim()) return songs;
    const term = searchTerm.toLowerCase();
    return songs.filter(song =>
      song.name?.toLowerCase().includes(term) ||
      song.artist?.toLowerCase().includes(term)
    );
  }, [searchTerm, songs]);

  const handleToggleSong = (song) => {
    setSelectedSongs(prev => {
      const isSelected = prev.some(s => s.songs_master_id === song.songs_master_id);
      if (isSelected) {
        return prev.filter(s => s.songs_master_id !== song.songs_master_id);
      } else {
        return [...prev, song];
      }
    });
  };

  const handleSave = async () => {
    if (selectedSongs.length === 0) {
      enqueueSnackbar('Please select at least one song', { variant: 'warning' });
      return;
    }

    if (!selectedSetId && availableSets.length > 0) {
      enqueueSnackbar('Please select a set', { variant: 'warning' });
      return;
    }

    setLoading(true);
    try {
      await onSave(selectedSongs, selectedSetId);
      handleClose();
    } catch (error) {
      console.error('Error adding songs:', error);
      enqueueSnackbar('Failed to add songs', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedSongs([]);
    setSearchTerm('');
    setSelectedSetId(targetSetId);
    onClose();
  };

  const isSelected = (song) => {
    return selectedSongs.some(s => s.songs_master_id === song.songs_master_id);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1e1e1e',
          color: 'white',
        },
      }}
    >
      <DialogTitle sx={{ bgcolor: '#2c2c2c', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Add Songs to Set</Typography>
        <Button onClick={handleClose} sx={{ color: 'white', minWidth: 'auto' }}>
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: '#121212' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Set Selection (only if multiple sets available and no target set specified) */}
          {availableSets.length > 0 && !targetSetId && (
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'white' }}>Select Set</InputLabel>
              <Select
                value={selectedSetId || ''}
                onChange={(e) => setSelectedSetId(e.target.value)}
                label="Select Set"
                sx={{ color: 'white' }}
              >
                {availableSets.map(set => (
                  <MenuItem key={set.set_id} value={set.set_id}>
                    {set.set_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Search Bar */}
          <TextField
            label="Search Songs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: '#666',
                },
                '&:hover fieldset': {
                  borderColor: '#888',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#4caf50',
                },
              },
              '& .MuiInputLabel-root': {
                color: '#999',
              },
            }}
          />

          {/* Selected Count */}
          {selectedSongs.length > 0 && (
            <Typography variant="body2" sx={{ color: '#999' }}>
              {selectedSongs.length} song(s) selected
            </Typography>
          )}

          {/* Songs List */}
          {loadingSongs ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                maxHeight: 400,
                overflowY: 'auto',
                border: '1px solid #333',
                borderRadius: 1,
              }}
            >
              {filteredSongs.length === 0 ? (
                <Typography sx={{ color: '#999', textAlign: 'center', py: 4 }}>
                  {searchTerm ? 'No songs found matching your search' : 'No songs available'}
                </Typography>
              ) : (
                <List>
                  {filteredSongs.map((song) => (
                    <ListItem key={song.songs_master_id} disablePadding>
                      <ListItemButton
                        onClick={() => handleToggleSong(song)}
                        sx={{
                          '&:hover': {
                            bgcolor: '#2a2a2a',
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={isSelected(song)}
                            tabIndex={-1}
                            sx={{
                              color: '#666',
                              '&.Mui-checked': {
                                color: '#4caf50',
                              },
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={song.name || 'Unknown Song'}
                          secondary={song.artist || 'Unknown Artist'}
                          sx={{
                            color: 'white',
                            '& .MuiListItemText-secondary': {
                              color: '#999',
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ bgcolor: '#2c2c2c', px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} sx={{ color: 'white' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || selectedSongs.length === 0}
          sx={{
            bgcolor: '#4caf50',
            '&:hover': { bgcolor: '#45a049' },
          }}
        >
          {loading ? 'Adding...' : `Add ${selectedSongs.length} Song(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSongModal;

